/**
 * Plugin de Inicialização do Sistema de Cache Otimizado
 *
 * Configura automaticamente:
 * - Query Client com configurações otimizadas
 * - Cleanup automático de stores
 * - Monitor de cache em desenvolvimento
 *
 * @since 2025-10-03
 */

import { queryClientConfig, CacheInvalidator, CacheMonitor } from "~/lib/cache-config";

export default defineNuxtPlugin(() => {
  // ========================================
  // 1. CONFIGURAÇÃO DO QUERY CLIENT
  // ========================================
  const queryClient = useQueryClient();

  // Aplica configurações otimizadas
  queryClient.setDefaultOptions(queryClientConfig.defaultOptions);

  // ========================================
  // 2. INICIALIZAÇÃO DE UTILITÁRIOS
  // ========================================
  const cacheInvalidator = new CacheInvalidator(queryClient);
  const cacheMonitor = new CacheMonitor();

  // ========================================
  // 3. INSTRUMENTAÇÃO DE DESENVOLVIMENTO
  // ========================================
  if (process.env.NODE_ENV === "development") {
    // Intercepta queries para monitoramento
    queryClient.getQueryCache().subscribe((event) => {
      if (event?.type === "updated") {
        const query = event.query;
        if (query.state.data !== undefined) {
          cacheMonitor.logCacheHit(query.queryKey as string[]);
        } else if (query.state.error) {
          cacheMonitor.logError(query.state.error, query.queryKey as string[]);
        }
      }
    });

    // Expor utilitários para debug em desenvolvimento
    if (import.meta.dev) {
      (window as typeof window & { __cacheUtils: unknown }).__cacheUtils = {
        // Função principal para estatísticas
        getStats: () => {
          const stats = cacheMonitor.getStats();
          const queriesCount = queryClient.getQueryCache().getAll().length;
          return {
            hitRate: stats.hits / (stats.hits + stats.misses) || 0,
            totalQueries: stats.hits + stats.misses,
            hits: stats.hits,
            misses: stats.misses,
            queriesCount,
          };
        },

        // Função para listar queries ativas
        getQueries: () => {
          return queryClient
            .getQueryCache()
            .getAll()
            .map((q) => ({
              queryKey: q.queryKey,
              state: q.state.status,
              dataUpdatedAt: q.state.dataUpdatedAt,
              isStale: q.isStale(),
              data: q.state.data ? "Loaded" : "Empty",
            }));
        },

        // Limpeza de cache
        clearAll: () => {
          queryClient.clear();
          console.info("🧹 All cache cleared!");
        },

        // Limpeza específica
        clearCustomers: () => {
          queryClient.invalidateQueries({ queryKey: ["customers"] });
          console.info("🧹 Customer cache cleared!");
        },

        debug: () => {
          const stats = cacheMonitor.getStats();
          const queries = queryClient.getQueryCache().getAll();
          console.table(stats);
          console.table(
            queries.map((q) => ({
              key: JSON.stringify(q.queryKey),
              status: q.state.status,
              stale: q.isStale(),
            })),
          );
        },

        // Performance monitoring
        performance: {
          getReport: () => {
            return {
              message: "Performance monitoring ativo. Use window.__cacheUtils.performance.getReport() para relatórios detalhados.",
              timestamp: new Date().toISOString(),
              queriesInCache: queryClient.getQueryCache().getAll().length,
              cacheSize: JSON.stringify(queryClient.getQueryCache().getAll()).length,
            };
          },
        },

        // Referências internas
        invalidator: cacheInvalidator,
        monitor: cacheMonitor,
        queryClient,
      };
    }

    // Log inicial
    console.info("🚀 Cache System Initialized", {
      defaultStaleTime: queryClientConfig.defaultOptions.queries.staleTime,
      defaultGcTime: queryClientConfig.defaultOptions.queries.gcTime,
    });
  }

  // ========================================
  // 4. DISPONIBILIZAR GLOBALMENTE
  // ========================================
  return {
    provide: {
      cacheInvalidator,
      cacheMonitor,
    },
  };
});
